import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Pagination, Row, Select, Space, Table, Typography, message, Checkbox } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./InformationTable.css";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { useShallow } from "zustand/react/shallow";
import { PlusCircleOutlined } from "@ant-design/icons";
import { replaceUndefinedWithNull, trimAllKey } from "src/utils/utils-object";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import { profileStore } from "src/stores/profile-store";
import VocsModal from "src/components/Modal";
import { setupWasteWaterStore } from "src/stores/waste-water-info-store";

import { utilsStore } from "src/stores/utils-store";
import { processEmissionObject } from "src/entity/waste-water/waste-water.constant";
import { ReportStatusEnum } from "src/entity/report-voc-emission/report-voc-emission.entity";
import useNavigationMenu from "src/hook/useNavigationMenu";

import { convertEmissionWord } from "src/entity/common/global.entity";
import { optionsSelectYears } from "src/utils/array";
import NumberRow from "./NumberRow";
import InformationModal from "./InformationModal";
import {
  OthersEmission,
  OthersInformationBody,
  PatchOthersInformationBody,
  QueryOthersInformation,
  ResponseOthers,
} from "src/entity/others/information";
import { canEditTable, canViewTable } from "../../PermissionCheck/PermissionCheck";
import { othersStore } from "src/stores/others-store";
import { isNull, omitBy } from "lodash";
import { EmissionObject } from "src/entity/common/unit.constant";

const { Text } = Typography;
interface OthersInformationTableProps {}

const OthersInformationTable: React.FC<OthersInformationTableProps> = ({}) => {
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [visibleModal, setVisibleModal] = useState(false);
  const [isEdit, setIsEdit] = useState<ResponseOthers | null>(null);
  const [tableKey, setTableKey] = useState(0);

  const { permissions } = useNavigationMenu();

  const profile = profileStore(useShallow((state) => state.profile));
  const getAreaInCompany = utilsStore(useShallow((state) => state.getAreaInCompany));
  const dataAreaInCompany = utilsStore(useShallow((state) => state.dataAreaInCompany));

  const searchOthersInformation = othersStore(useShallow((state) => state.getOthersInformation));
  const dataOthers = othersStore(useShallow((state) => state.dataOthers));
  const totalDocs = dataOthers.total;

  const getFilterColumnOtherInfo = othersStore(useShallow((state) => state.getAllDistinctColumns));
  const filterOthers = othersStore(useShallow((state) => state.dataFilterColumns));

  const allStoreLoading = othersStore(useShallow((state) => state.loading));

  const createOthersInfo = othersStore(useShallow((state) => state.createOthersInformation));
  const deleteOthersInfo = othersStore(useShallow((state) => state.deleteOtherInformation));
  const updateOthersInfo = othersStore(useShallow((state) => state.updateOthersInformation));

  const getMasterEmissionObject = setupWasteWaterStore(useShallow((state) => state.getMasterEmissionObject));
  const dataMasterEmissionObject = setupWasteWaterStore(useShallow((state) => state.dataMasterEmissionObject));

  const clearError = othersStore(useShallow((state) => state.clearError));
  const errorOthers = othersStore(useShallow((state) => state.error));

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";

    if (type === "area" && dataAreaInCompany) {
      const optValue = dataAreaInCompany.find((item) => item._id === text);
      return optValue?.areaName;
    } else return text;
  };

  const onClickEdit = (item: ResponseOthers) => {
    const emissionLists = [];
    for (const key in item.emission) {
      if (item.emission[key as keyof typeof item.emission].active) emissionLists.push(key);
    }
    form.setFieldsValue({
      area: getMasterLabel("area", item.area) ? item.area : undefined,
      sourceName: item.sourceName,
      emission: emissionLists,
      calculationMethod: item.calculationMethod,
    });
    setIsEdit(item);
    setVisibleModal(true);
  };

  const deleteSetting = async (id: string) => {
    const confirm = await VocsModal.delete({
      message: "",
    });
    if (!confirm) return;
    deleteOthersInfo(id).then(() => {
      onFinishAction();
    });
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    data: sourceData,
  } = usePagination<ResponseOthers, QueryOthersInformation>({
    action: searchOthersInformation,
    remoteData: dataOthers.data,
    initialFilter: { year: [selectedYear] },
  });

  const currentFilterExcludePage = useMemo(() => {
    const result = omitBy(filter, (value, key) => key === "page" || key === "year" || key === "limit" || isNull(value));
    return Object.keys(result).length;
  }, [filter]);

  const sourceNameFilter = useMemo(() => {
    if (filterOthers.sourceName?.length === 0 && currentFilterExcludePage == 0) return [];
    return filterOthers.sourceName.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterOthers.sourceName, dataOthers.data]);

  const areaFilter = useMemo(() => {
    if (dataOthers.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return filterOthers.area.map((item) => {
      const findLabel = dataAreaInCompany.find((e) => e._id === item);
      return {
        text: findLabel?.areaName,
        value: item,
      };
    });
  }, [filterOthers.area, dataOthers.data, dataAreaInCompany]);

  const handleTableChange: TableProps<ResponseOthers>["onChange"] = (_, filters) => {
    onChange(filters);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseOthers>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, record, index) => <NumberRow record={record} no={getIndex(index + 1)} dataAreaInCompany={dataAreaInCompany} />,
      align: "left",
    },

    {
      title: "Source Name",
      dataIndex: "sourceName",
      key: "sourceName",
      render: (_, record) => <Text>{record.sourceName}</Text>,
      filters: sourceNameFilter,
      align: "left",
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      render: (text) => <Text>{getMasterLabel("area", text)}</Text>,
      filters: areaFilter,
      align: "left",
    },
    {
      title: "Emission",
      children:
        dataMasterEmissionObject.length > 0
          ? dataMasterEmissionObject[0].options.map((item) => ({
              title: convertEmissionWord(item.label),
              dataIndex: "emission",
              width: "3rem",
              align: "left",
              render: (value) => (
                <div>
                  <Checkbox checked={value[item.value].active} disabled={true}></Checkbox>
                </div>
              ),
            }))
          : [],
      align: "center",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle" direction={"horizontal"}>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => onClickEdit(record)}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={18} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => deleteSetting(record._id)}
            disabled={
              !canEditTable(permissions) ||
              JSON.stringify(record.vocEmission).includes(ReportStatusEnum.REQUEST) ||
              JSON.stringify(record.vocEmission).includes(ReportStatusEnum.APPROVER_1) ||
              JSON.stringify(record.vocEmission).includes(ReportStatusEnum.APPROVER_2) ||
              JSON.stringify(record.vocEmission).includes(ReportStatusEnum.APPROVER_3)
            }
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
      align: "left",
    },
  ];

  const getColumnFilter = () => {
    getFilterColumnOtherInfo(selectedYear);
  };

  const initialRequestDidMount = (isDidMount?: boolean) => {
    getAreaInCompany(profile?.activeCompany._id as string);
    getColumnFilter();
    searchOthersInformation(
      isDidMount === true ? { ...filter, page, limit: pageSize, year: [selectedYear] } : { page, limit: pageSize, year: [selectedYear] },
    );
    getMasterEmissionObject();
  };

  useDidMount(() => {
    initialRequestDidMount(true);
  });

  useEffect(() => {
    if (profile?.activeCompany) {
      setPage(1);
      setTableKey(tableKey + 1);
    }
  }, [profile?.activeCompany, selectedYear]);
  useEffect(() => {
    initialRequestDidMount(false);
  }, [tableKey]);

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const onAddOthersInfo = () => {
    form.setFieldValue("emission", ["tvoc"]);
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    form.resetFields();
    clearError();
    setIsEdit(null);
  };

  const mapEmissionObject = (emission: string[], isEdit: ResponseOthers | null) => {
    const responseObject = !isEdit?.emission ? { ...processEmissionObject } : isEdit.emission;
    EmissionObject.forEach((item) => {
      if (emission.includes(item.value))
        responseObject[`${item.value as keyof OthersEmission}`] = {
          ...responseObject[`${item.value as keyof OthersEmission}`],
          active: true,
        };
      else
        responseObject[`${item.value as keyof OthersEmission}`] = {
          ...responseObject[`${item.value as keyof OthersEmission}`],
          active: false,
        };
    });
    return responseObject;
  };

  const submitOthersInformation = async () => {
    let checkError = false;
    await form.validateFields().catch(() => {
      checkError = true;
    });
    if (checkError) return;

    const formData = form.getFieldsValue();
    // console.log("form data ; ", formData);
    const formatData = trimAllKey(replaceUndefinedWithNull(formData), []);
    const finalData = {
      ...formatData,
      emission: mapEmissionObject(formatData.emission, isEdit),
      companyId: profile?.activeCompany._id as string,
      year: selectedYear,
      isActive: true,
    };

    // console.log("Final data : ", finalData);
    if (isEdit) {
      updateOthersInfo({ ...finalData, _id: isEdit._id } as PatchOthersInformationBody).then(onFinishAction);
    } else createOthersInfo(finalData as OthersInformationBody).then(onFinishAction);
  };

  const handleChangeYear = (year: string) => {
    // console.log("Year : ", year);
    onChange({ ...filter, year: [year] });
    setSelectedYear(year);
  };

  const onFinishAction = (response?: unknown) => {
    // console.log("response : ", response);
    if (typeof response === "string") {
      // warningAlert(response as string);
      form.setFields([
        {
          name: "sourceName",
          errors: [""],
        },
        {
          name: "area",
          errors: [""],
        },
      ]);
      return;
    } else {
      // if (isEdit?._id) calculateReportVocWasteWater(isEdit._id);
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      getColumnFilter();
      searchOthersInformation({ ...filter, page, limit: pageSize, year: [selectedYear] });
      closeModal();
    }
  };

  const processRowClassName = useCallback(
    (record: ResponseOthers) => {
      const hasArea = dataAreaInCompany.find((item) => item._id === record.area);
      return !hasArea ? "red-border-row" : "";
    },
    [selectedYear, sourceData, dataAreaInCompany],
  );

  return (
    <Row className="w-full" id={"wrap-other-information-table"}>
      {contextHolder}
      <Row className="w-full" justify={"space-between"}>
        <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
        <Button
          type={"primary"}
          size={"middle"}
          className="flex items-center"
          onClick={onAddOthersInfo}
          style={canEditTable(permissions) ? {} : { display: "none" }}
        >
          <PlusCircleOutlined />
          New
        </Button>
      </Row>
      <Table
        key={tableKey}
        columns={columns}
        dataSource={canViewTable(permissions) ? (sourceData as ResponseOthers[]) : []}
        className="w-full mt-4"
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        rowKey={(rec) => `${rec._id}`}
        rowClassName={processRowClassName} // Apply red border conditionally
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      {visibleModal && (
        <InformationModal
          dataAreaInCompany={dataAreaInCompany}
          submitOthersInformation={submitOthersInformation}
          loadingAll={allStoreLoading}
          visibleModal={visibleModal}
          closeModal={closeModal}
          errorOthers={errorOthers}
          form={form}
          clearErrorOthers={clearError}
        />
      )}
    </Row>
  );
};

export default OthersInformationTable;
